import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  AnnualReportApi,
  DiagnosisExplanation,
  RemoteServiceDiagnosisQueryParams,
  RemoteServiceQueryParams,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';
import moment from 'moment';
import { Observable, combineLatest, of } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  mergeMap,
  retryWhen,
  scan,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import {
  RemoteServiceApi,
  RemoteServiceChartConfig,
  RemoteServiceDiagnosisResponse,
  RemoteServiceFeatures,
  RemoteServiceRecommendations,
} from '@vi-iot/client';

import { AuthStore } from '@store';

import { DevicesStore } from '../devices';
import { InstallationDetailsStore } from '../installation-details';

import {
  AppInsightsMonitoringService,
  LogLevelEnum,
} from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_REMOTE_SERVICE } from 'src/app/shared/services/app-insights/events/remote-service.events';

import { RemoteServiceActions } from './remote-service.actions';
import { RemoteServiceMapper } from './remote-service.mapper';
import { chartsConfigMock } from './remote-service.mock';
import {
  ANNUAL_REPORT_ERROR_TRANSLATIONS,
  AnnualReportErrors,
  RemoteServiceResponseErrorType,
  remoteServiceIgnoreNotificationKey,
} from './remote-service.model';
import { RemoteServiceStore } from './remote-service.store';

@Injectable()
export class RemoteServiceEffects {
  loadAllSummary$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.loadAllSummary),
      tap(() => {
        this.appInsights.logEvent(APP_INSIGHTS_REMOTE_SERVICE.GET_SUMMARY, LogLevelEnum.DEBUG);
      }),
      switchMap((action) =>
        this.remoteServiceApi.getRecommendations(action.boilerSerials).pipe(
          map((summaryData: { data: RemoteServiceRecommendations[] }) =>
            RemoteServiceActions.loadAllSummarySuccess({
              summary: [].concat(summaryData.data),
            })
          ),
          catchError((error) => {
            this.appInsights.logEvent(
              APP_INSIGHTS_REMOTE_SERVICE.GET_SUMMARY_ERROR,
              LogLevelEnum.ERROR,
              { error }
            );

            return of(RemoteServiceActions.loadAllSummaryError({ error }));
          })
        )
      )
    );
  });

  loadChartFeatures$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.loadChartFeatures),
      tap(() => {
        this.appInsights.logEvent(APP_INSIGHTS_REMOTE_SERVICE.GET_FEATURES, LogLevelEnum.DEBUG);
      }),
      concatLatestFrom(() => [
        this.authStore.isDemo$,
        this.devicesStore.mainDeviceBoilerSerial$,
        this.remoteServiceStore.chartExtremes$,
      ]),
      switchMap(([action, isDemo, mainDeviceBoilerSerial, extremes]) => {
        const params: RemoteServiceQueryParams = {
          boiler_id: action.deviceSerial ?? mainDeviceBoilerSerial,
          start_datetime: moment(extremes.startPointMs).format('YYYY-MM-DDTHH:mm:ss'),
          end_datetime: moment(extremes.endPointMs).format('YYYY-MM-DDTHH:mm:ss'),
        };

        return this.remoteServiceApi.getFeatures(params).pipe(
          map((chartsData: { data: RemoteServiceFeatures }) => {
            // When no supported feature is found within a request we return an error
            if (!chartsData.data?.series?.length) {
              return RemoteServiceActions.loadChartFeaturesError({
                error: { errorType: RemoteServiceResponseErrorType.DataNotFound },
              });
            } else {
              // It should stay until VIG-8475 is done
              if (isDemo) {
                chartsData.data.point_start = moment(params.start_datetime)
                  .subtract(1, 'd')
                  .valueOf();
                const startDate = moment(params.start_datetime).subtract(1, 'd');
                const endDate = moment(params.end_datetime);
                const dateDiffDays = endDate.diff(startDate, 'd');
                const numberOfMockedDays = 30;

                if (dateDiffDays < numberOfMockedDays) {
                  // Cut data - we receive hardcoded 30 days
                  chartsData.data.series = chartsData.data.series.map((series) => ({
                    ...series,
                    data: series.data.slice(-dateDiffDays),
                  }));
                }

                if (dateDiffDays > numberOfMockedDays) {
                  // Fill with empty data from the left side of chart - simulate lack of old data
                  const numberOfEmptyEntriesToAdd = dateDiffDays - numberOfMockedDays;

                  chartsData.data.series = chartsData.data.series.map((series) => ({
                    ...series,
                    data: [...Array(numberOfEmptyEntriesToAdd).fill(null), ...series.data],
                  }));
                }
              }

              return RemoteServiceActions.loadChartFeaturesSuccess({
                chartsData: chartsData.data,
              });
            }
          }),
          catchError((error) => {
            this.appInsights.logEvent(
              APP_INSIGHTS_REMOTE_SERVICE.GET_FEATURES_ERROR,
              LogLevelEnum.ERROR,
              { error }
            );

            return of(RemoteServiceActions.loadChartFeaturesError({ error }));
          })
        );
      }),
      catchError((error) => {
        return of(RemoteServiceActions.loadChartFeaturesError({ error }));
      })
    );
  });

  loadDiagnosis$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.loadDiagnosis),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.devicesStore.selectedDevice$.pipe(take(1)),
          this.authStore.isRsaUser(),
          this.authStore.isMaintainer$,
        ])
      ),
      tap(([_action, device]) => {
        if (device?.boilerSerial) {
          this.appInsights.logEvent(APP_INSIGHTS_REMOTE_SERVICE.GET_DIAGNOSIS, LogLevelEnum.DEBUG);
        }
      }),
      switchMap(([action, device, isRsa, isMaintainer]) => {
        if (!device.boilerSerial) {
          return of(
            RemoteServiceActions.loadDiagnosisError({
              error: 'device is has no devices or missing boiler serial, dont fetch diagnosis',
            })
          );
        }

        const params: RemoteServiceDiagnosisQueryParams = {
          boiler_id: action.deviceSerial ?? device.boilerSerial,
          start_datetime: moment().subtract(2, 'days').endOf('day').toISOString(),
          end_datetime: moment().startOf('day').toISOString(),
        };

        return this.remoteServiceApi.getDiagnosis(params).pipe(
          switchMap((diagnosis: RemoteServiceDiagnosisResponse) => {
            const diagnosisData = RemoteServiceMapper.normalizeDiagnosisData(diagnosis, {
              isRsa,
              isMaintainer,
            });

            return of(
              RemoteServiceActions.loadDiagnosisSuccess({
                diagnosisData,
              })
            );
          }),
          catchError((error) => {
            this.appInsights.logEvent(
              APP_INSIGHTS_REMOTE_SERVICE.GET_DIAGNOSIS_ERROR,
              LogLevelEnum.ERROR,
              { error }
            );

            return of(RemoteServiceActions.loadDiagnosisError({ error }));
          })
        );
      }),
      catchError((error) => {
        return of(RemoteServiceActions.loadDiagnosisError({ error }));
      })
    );
  });

  loadChartsConfig$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.loadChartsConfig),
      switchMap(() =>
        of(chartsConfigMock).pipe(
          map((chartsConfig: RemoteServiceChartConfig[]) =>
            RemoteServiceActions.loadChartsConfigSuccess({ chartsConfig })
          ),
          catchError((error) => of(RemoteServiceActions.loadChartsConfigError({ error })))
        )
      )
    );
  });

  saveThreshold$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.saveThreshold),
      tap(() => {
        this.appInsights.logEvent(APP_INSIGHTS_REMOTE_SERVICE.SAVE_THRESHOLD, LogLevelEnum.DEBUG);
      }),
      switchMap((action) =>
        this.remoteServiceApi.saveThreshold(action.params).pipe(
          map(() => RemoteServiceActions.saveThresholdSuccess({ params: action.params })),
          catchError((error) => {
            this.appInsights.logEvent(
              APP_INSIGHTS_REMOTE_SERVICE.SAVE_THRESHOLD_ERROR,
              LogLevelEnum.ERROR,
              { error }
            );

            return of(RemoteServiceActions.saveThresholdError({ error }));
          })
        )
      )
    );
  });

  getAnnualReport$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.getAnnualReport),
      concatLatestFrom(() => this.remoteServiceStore.annualReportState$),
      switchMap(([action, state]) => {
        if (
          !state.deviceSerial ||
          !state.gatewaySerial ||
          !state.reportTemplate ||
          state.deviceSerial !== action.deviceSerial ||
          state.gatewaySerial !== action.gatewaySerial ||
          state.reportTemplate !== action.reportTemplate ||
          !state.fileUri ||
          !state.processId ||
          state.error
        ) {
          return of(
            RemoteServiceActions.createAnnualReport({
              reportTemplate: action.reportTemplate,
              gatewaySerial: action.gatewaySerial,
              deviceSerial: action.deviceSerial,
            })
          );
        }

        return of(RemoteServiceActions.getAnnualReportUrl());
      }),
      catchError(() => {
        return of(null);
      })
    );
  });

  createAnnualReport$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.createAnnualReport),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.installationDetailsStore.getSelectedGateway$,
          this.devicesStore.getDeviceId(action.deviceSerial),
          this.devicesStore.selectedDevice$,
        ]).pipe(take(1))
      ),
      tap(() => {
        this.appInsights.logEvent(
          APP_INSIGHTS_REMOTE_SERVICE.CREATE_ANNUAL_REPORT,
          LogLevelEnum.DEBUG
        );
      }),
      switchMap(([action, gateway, deviceId, selectedDevice]) => {
        return this.annualReportApi
          .createReportOnDemand(
            gateway.installationId,
            action.gatewaySerial,
            deviceId ?? selectedDevice.id,
            action.reportTemplate
          )
          .pipe(
            map((response) =>
              RemoteServiceActions.createAnnualReportSuccess({
                processId: response.data.processId,
              })
            ),
            catchError((error) => {
              this.showAnnualReportErrorToast(error);
              this.appInsights.logEvent(
                APP_INSIGHTS_REMOTE_SERVICE.CREATE_ANNUAL_REPORT_ERROR,
                LogLevelEnum.ERROR,
                { error }
              );

              return of(RemoteServiceActions.createAnnualReportError({ error }));
            })
          );
      })
    );
  });

  getAnnualReportUrl$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RemoteServiceActions.getAnnualReportUrl,
        RemoteServiceActions.createAnnualReportSuccess
      ),
      concatLatestFrom(() => this.remoteServiceStore.annualReportState$),
      switchMap(([_action, state]) =>
        this.annualReportApi.getReport(state.processId).pipe(
          // Retry if errors using 5s interval, at 10th attempt throw an error
          retryWhen((errors) =>
            errors.pipe(
              scan((retryIndex, error) => {
                if (retryIndex === 10 || error.errorType !== AnnualReportErrors.ReportNotReady) {
                  throw error;
                }

                return retryIndex + 1;
              }, 1),
              delay(5000)
            )
          ),
          take(1),
          map((response) => {
            return RemoteServiceActions.getAnnualReportUrlSuccess({
              fileUri: response.data.fileUri,
            });
          }),
          catchError((error) => {
            this.showAnnualReportErrorToast(error);

            return of(RemoteServiceActions.getAnnualReportUrlError({ error }));
          })
        )
      )
    );
  });

  hideLowBudgetNotification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.hideLowBudgetNotification),
      concatLatestFrom(() => [
        this.installationDetailsStore.gatewaySerial$,
        this.remoteServiceStore.ignoredRemoteServiceNotifications$,
      ]),
      map(([_action, gatewaySerial, currentlyIgnoredRemoteServiceNotifications]) => {
        const ignoredRemoteServiceNotifications = [
          ...currentlyIgnoredRemoteServiceNotifications,
          gatewaySerial,
        ];

        localStorage.setItem(
          remoteServiceIgnoreNotificationKey,
          JSON.stringify(ignoredRemoteServiceNotifications)
        );

        return RemoteServiceActions.getLowBudgetNotification();
      })
    );
  });

  loadDiagnosisWhitelist$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.loadDiagnosisWhitelist.attempt),
      switchMap((action) =>
        this.remoteServiceApi.getDiagnosisWhitelist(action.boilerId).pipe(
          map((response) =>
            RemoteServiceActions.loadDiagnosisWhitelist.success({
              diagnoses: response.data.diagnoses,
            })
          ),
          catchError((error) => of(RemoteServiceActions.loadDiagnosisWhitelist.error({ error })))
        )
      )
    );
  });

  loadDiagnosisExplanation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.loadDiagnosisExplanation.request),
      concatLatestFrom((action) =>
        this.remoteServiceStore.getDiagnosisExplanation(action.diagnosisName)
      ),
      filter(([, explanation]) => !explanation),
      map(([action]) =>
        RemoteServiceActions.loadDiagnosisExplanation.attempt({
          diagnosisName: action.diagnosisName,
          boilerId: action.boilerId,
          lang: action.lang,
          startDatetime: action.startDatetime,
          endDatetime: action.endDatetime,
        })
      )
    );
  });

  loadDiagnosisExplanationAttempt$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteServiceActions.loadDiagnosisExplanation.attempt),
      mergeMap((action) =>
        this.remoteServiceApi
          .getDiagnosisExplanation({
            boiler_id: action.boilerId,
            diagnosis_name: action.diagnosisName,
            lang: action.lang,
            start_datetime: action.startDatetime,
            end_datetime: action.endDatetime,
          })
          .pipe(
            map((explanation: DiagnosisExplanation) =>
              RemoteServiceActions.loadDiagnosisExplanation.success({
                diagnosisName: action.diagnosisName,
                explanation,
              })
            ),
            catchError((error) =>
              of(
                RemoteServiceActions.loadDiagnosisExplanation.error({
                  diagnosisName: action.diagnosisName,
                  error,
                })
              )
            )
          )
      )
    );
  });

  private showAnnualReportErrorToast(error: any): void {
    if (error?.errorType && Object.values(AnnualReportErrors).includes(error.errorType)) {
      this.toastService.error({
        message: this.translateService.instant(ANNUAL_REPORT_ERROR_TRANSLATIONS[error.errorType]),
      });
    }
  }

  constructor(
    private actions$: Actions,
    private remoteServiceApi: RemoteServiceApi,
    private annualReportApi: AnnualReportApi,
    private remoteServiceStore: RemoteServiceStore,
    private installationDetailsStore: InstallationDetailsStore,
    private devicesStore: DevicesStore,
    private toastService: ViToastService,
    private translateService: TranslateService,
    private appInsights: AppInsightsMonitoringService,
    private authStore: AuthStore
  ) {}
}
